import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import * as THREE from "three";
import { cn } from "lib/utils";
import { THEME_ENUM } from "constants/theme.constant";

/**
 * Logo icon gradient — `public/img/logo/logoblack.svg`
 * Uses THREE.Color so vertex colors match Three.js color management (r152+).
 */
const LOGO_C0 = /* #1b5fcc */ new THREE.Color("#1b5fcc");
const LOGO_C1 = /* #d557aa */ new THREE.Color("#d557aa");
const LOGO_C2 = /* #4d2584 */ new THREE.Color("#4d2584");

const _sampleColor = new THREE.Color();

function sampleLogoGradient(t) {
  const x = Math.min(1, Math.max(0, t));
  if (x <= 0.64) {
    _sampleColor.lerpColors(LOGO_C0, LOGO_C1, x / 0.64);
  } else if (x <= 0.97) {
    _sampleColor.lerpColors(LOGO_C1, LOGO_C2, (x - 0.64) / (0.97 - 0.64));
  } else {
    _sampleColor.copy(LOGO_C2);
  }
  return _sampleColor;
}

/** Lighten for dark UI while keeping pink/purple/blue bias */
function tintForDarkMode(color, out) {
  out.copy(color);
  out.r = Math.min(1, color.r * 0.6 + 0.38);
  out.g = Math.min(1, color.g * 0.6 + 0.42);
  out.b = Math.min(1, color.b * 0.6 + 0.52);
  return out;
}

const _vertexOut = new THREE.Color();

/**
 * Animated dotted/point grid background (Three.js).
 * Theme follows Redux `state.theme.mode` (same as rest of app); no next-themes required.
 */
export function DottedSurface({ className, ...props }) {
  const mode = useSelector((state) => state.theme.mode);
  const isDark = mode === THEME_ENUM.MODE_DARK;

  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const SEPARATION = 150;
    const AMOUNTX = 40;
    const AMOUNTY = 60;

    const scene = new THREE.Scene();
    // No fog — THREE.Fog blends points toward fogColor and washes out vertexColors.

    const camera = new THREE.PerspectiveCamera(60, 1, 1, 10000);
    camera.position.set(0, 355, 1220);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
    });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const geometry = new THREE.BufferGeometry();
    const positions = [];
    const colors = [];

    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        const x = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2;
        const y = 0;
        const z = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2;
        positions.push(x, y, z);

        const tx = ix / Math.max(1, AMOUNTX - 1);
        const ty = iy / Math.max(1, AMOUNTY - 1);
        const t =
          ((tx + ty) / 2) * 0.95 +
          (Math.sin(ix * 0.35) + Math.cos(iy * 0.28)) * 0.025;

        const base = sampleLogoGradient(t);
        if (isDark) {
          tintForDarkMode(base, _vertexOut);
        } else {
          /* Full saturation on white background */
          _vertexOut.copy(base);
        }
        colors.push(_vertexOut.r, _vertexOut.g, _vertexOut.b);
      }
    }

    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3),
    );
    geometry.setAttribute(
      "color",
      new THREE.Float32BufferAttribute(colors, 3),
    );

    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 10,
      vertexColors: true,
      transparent: true,
      opacity: 0.92,
      sizeAttenuation: true,
      depthWrite: false,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    let count = 0;
    let animationId = 0;

    const fit = () => {
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    const tick = () => {
      animationId = requestAnimationFrame(tick);
      if (document.hidden) return;

      const positionAttribute = geometry.attributes.position;
      const pos = positionAttribute.array;

      let i = 0;
      for (let ix = 0; ix < AMOUNTX; ix++) {
        for (let iy = 0; iy < AMOUNTY; iy++) {
          const index = i * 3;
          pos[index + 1] =
            Math.sin((ix + count) * 0.3) * 50 +
            Math.sin((iy + count) * 0.5) * 50;
          i++;
        }
      }
      positionAttribute.needsUpdate = true;
      renderer.render(scene, camera);
      count += 0.1;
    };

    const pauseLoop = () => {
      cancelAnimationFrame(animationId);
      animationId = 0;
    };

    const resumeLoop = () => {
      if (animationId !== 0 || document.hidden) return;
      tick();
    };

    const onVisibility = () => {
      if (document.hidden) pauseLoop();
      else resumeLoop();
    };

    document.addEventListener("visibilitychange", onVisibility);

    const ro = new ResizeObserver(() => {
      fit();
    });
    ro.observe(container);
    fit();
    tick();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      pauseLoop();
      ro.disconnect();

      scene.traverse((object) => {
        if (object instanceof THREE.Points) {
          object.geometry.dispose();
          const mat = object.material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else if (mat) {
            mat.dispose();
          }
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [isDark]);

  return (
    <div
      ref={containerRef}
      className={cn("pointer-events-none h-full w-full min-h-full", className)}
      {...props}
    />
  );
}

export default DottedSurface;
